import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  ArrowsClockwiseIcon,
  CaretRightIcon,
  CoinsIcon,
  CreditCardIcon,
  EyeIcon,
  EyeSlashIcon,
  MoneyIcon,
  TargetIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';

import Link from '@/components/ui/Link';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TooltipContent, TooltipRoot, TooltipTrigger } from '@/components/ui/tooltip';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { useAvailableToSpend } from '@/hooks/queries/useAvailableToSpend';
import { useSpendingPace } from '@/hooks/queries/useSpendingPace';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { trackEvent } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import type { AvailableToSpend, AvailableToSpendAccount, AvailableToSpendDay } from '@/types';
import { dailyMetaParts, formatDayMonth, selectDailyMeta } from './dailyMeta';
import {
  isTimelineDayInteractive,
  timelineDayDescription,
  timelineDayMarkers,
  type TimelineMarker,
} from './disponivelTimeline';
import { WidgetCard, widgetState } from './WidgetCard';
import { WidgetInfoButton, WidgetInfoRow } from './WidgetInfoButton';

const MAX_ACCOUNT_CHIPS = 4;

/**
 * W1 · Disponível para gastar — the hero card: safe-to-spend until the next
 * salary (bank balances − open invoices − recurring commitments in the window).
 * One dark primary card at every breakpoint (signed off over the proto's light
 * desktop hero): mobile trades the timeline for the daily-goal footer, desktop
 * shows the day-by-day commitments timeline. Non-ready states fall back to the
 * standard WidgetCard chrome so loading/empty/error match every other widget.
 */
export function DisponivelWidget(): ReactNode {
  const { data, error, isLoading } = useAvailableToSpend();
  const state = widgetState(data, error, isLoading, isDisponivelEmpty);

  if (state !== 'ready' || !data) {
    return (
      <WidgetCard
        eyebrow="Disponível para gastar"
        action={{ label: 'Contas', href: '/connections' }}
        state={state}
        emptyLabel="Conecte uma conta para ver quanto pode gastar"
      />
    );
  }

  return <DisponivelCard data={data} />;
}

function isDisponivelEmpty(data: AvailableToSpend): boolean {
  return data.accounts.length === 0;
}

function DisponivelCard({ data }: { data: AvailableToSpend }): ReactNode {
  const formatCurrency = useFormatCurrency();
  const windowEndLabel = formatDayMonth(data.windowEnd);

  return (
    <section
      data-brand-fill
      className={cn(
        'relative flex h-full flex-col overflow-hidden rounded-3xl p-5 shadow-md',
        'bg-primary text-primary-foreground sm:p-7',
      )}
    >
      <HeroGlow />

      <header className="relative flex items-center justify-between gap-3">
        {/* The ⓘ rides right next to the title (user review). The title never
            truncates (mobile review): it wraps to a second line when narrow,
            and the window date stays atomic on its own no-break span. */}
        <span className="flex min-w-0 items-center gap-1.5">
          <p className="min-w-0 font-mono text-[0.7rem] font-medium tracking-[0.14em] text-primary-foreground/70 uppercase">
            Disponível para gastar
            {windowEndLabel ? (
              <span className="whitespace-nowrap">{` · até ${windowEndLabel}`}</span>
            ) : null}
          </p>
          <WidgetInfoButton
            widget="disponivel"
            label="Como o disponível é calculado"
            triggerClassName="text-primary-foreground/70 hover:text-primary-foreground"
          >
            <DisponivelInfo data={data} />
          </WidgetInfoButton>
        </span>
        <PrivacyToggle />
        <Link
          href="/connections"
          className="hidden shrink-0 items-center gap-0.5 text-xs font-medium text-primary-foreground/70 transition-colors hover:text-primary-foreground sm:flex"
        >
          Contas
          <CaretRightIcon size={12} weight="bold" aria-hidden />
        </Link>
      </header>

      <p
        className={cn(
          'relative mt-2.5 font-mono text-[3.25rem] leading-none font-semibold tracking-tight',
          'tabular-nums sm:mt-3.5 sm:text-[3.5rem]',
        )}
      >
        {formatCurrency(data.available, { noCents: true })}
      </p>

      <AccountChips accounts={data.accounts} />

      <div className="relative mt-3.5 flex flex-wrap gap-2 font-mono text-[11px] tabular-nums">
        <BreakdownChip label="Contas" amount={formatCurrency(data.bankTotal, { noCents: true })} />
        <BreakdownChip
          label="Fatura"
          amount={`−${formatCurrency(data.openInvoicesTotal, { noCents: true })}`}
          hiddenWhenZero={data.openInvoicesTotal}
        />
        <BreakdownChip
          label="Despesas previstas"
          amount={`−${formatCurrency(data.recurringTotal, { noCents: true })}`}
          hiddenWhenZero={data.recurringTotal}
        />
      </div>

      <CommitmentsTimeline data={data} />
      <DailyGoalFooter data={data} />
    </section>
  );
}

/**
 * The ⓘ breakdown (design review): the header chips show the raw parts, this
 * popover spells out the arithmetic behind "disponível" and the daily goal.
 */
function DisponivelInfo({ data }: { data: AvailableToSpend }): ReactNode {
  const formatCurrency = useFormatCurrency();
  const windowEndLabel = formatDayMonth(data.windowEnd);

  return (
    <div className="space-y-2.5">
      <p className="text-sm font-medium text-text-primary">Como chegamos neste valor</p>
      <dl className="space-y-1">
        <WidgetInfoRow
          label="Contas (saldo agregado)"
          value={formatCurrency(data.bankTotal, { noCents: true })}
        />
        {data.openInvoicesTotal > 0 && (
          <WidgetInfoRow
            label={data.hasEstimatedInvoices ? 'Fatura aberta (estimada)' : 'Fatura aberta'}
            value={`−${formatCurrency(data.openInvoicesTotal, { noCents: true })}`}
          />
        )}
        {data.recurringTotal > 0 && (
          <WidgetInfoRow
            label="Despesas previstas"
            value={`−${formatCurrency(data.recurringTotal, { noCents: true })}`}
          />
        )}
        <div className="flex items-baseline justify-between gap-3 border-t border-border-subtle pt-1">
          <dt className="text-xs font-medium text-text-primary">
            {windowEndLabel ? `Disponível até ${windowEndLabel}` : 'Disponível'}
          </dt>
          <dd className="shrink-0 font-mono text-xs font-semibold text-text-primary tabular-nums">
            {formatCurrency(data.available, { noCents: true })}
          </dd>
        </div>
      </dl>
      <p className="text-xs text-text-tertiary">
        Saldo das contas menos a fatura aberta e as despesas recorrentes previstas até o fim do
        ciclo{data.windowEndsOnSalary ? ' (o próximo salário)' : ''}. A meta diária segue a
        restrição mais apertada entre esse disponível e o ritmo planejado (orçamento ou média).
        {data.hasEstimatedInvoices
          ? ' A fatura aberta é estimada: o banco ainda não enviou a fatura fechada, e a anterior foi considerada paga pelo pagamento identificado.'
          : ''}
      </p>
    </div>
  );
}

/** The prototype's decorative radial glows, light-on-primary at every size. */
function HeroGlow(): ReactNode {
  return (
    <>
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -top-[70px] -right-[50px] size-[230px] rounded-full sm:size-[280px]',
          'bg-[radial-gradient(circle,color-mix(in_srgb,var(--primary-foreground)_16%,transparent),transparent_70%)]',
        )}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -bottom-[90px] -left-10 size-[200px] rounded-full',
          'bg-[radial-gradient(circle,color-mix(in_srgb,var(--primary-foreground)_10%,transparent),transparent_70%)]',
        )}
      />
    </>
  );
}

/** Mobile-only eye toggle in the card header (the prototype's dark hero has one). */
function PrivacyToggle(): ReactNode {
  const { hideValues, toggleHideValues } = usePrivacy();
  const Icon = hideValues ? EyeSlashIcon : EyeIcon;

  return (
    <button
      type="button"
      onClick={toggleHideValues}
      aria-label={hideValues ? 'Mostrar valores' : 'Ocultar valores'}
      className="-m-2 shrink-0 cursor-pointer p-2 text-primary-foreground/70 transition-colors hover:text-primary-foreground sm:hidden"
    >
      <Icon size={16} aria-hidden />
    </button>
  );
}

function AccountChips({ accounts }: { accounts: AvailableToSpendAccount[] }): ReactNode {
  const formatCurrency = useFormatCurrency();

  return (
    <div className="relative mt-2.5 flex items-center gap-2">
      <div className="flex">
        {accounts.slice(0, MAX_ACCOUNT_CHIPS).map((account, index) => (
          <AccountChip
            // Index key: several accounts of one Pluggy item share the connector
            // name (and often the balance); the list is a stable slice per render.
            key={index}
            account={account}
            title={`${account.name} · ${formatCurrency(account.balance, { noCents: true })}`}
          />
        ))}
      </div>
      <span className="text-[11px] text-primary-foreground/75">
        {accounts.length === 1 ? '1 conta agregada' : `${accounts.length} contas agregadas`}
      </span>
    </div>
  );
}

/** The bank's real brand icon (Pluggy connector image) when it exists; the colored
 * initial chip is only the fallback — an initial+brand-color guess reads as the wrong
 * bank ("Itaú" → orange "I" looks like Inter). */
function AccountChip({
  account,
  title,
}: {
  account: AvailableToSpendAccount;
  title: string;
}): ReactNode {
  if (account.imageUrl) {
    return (
      <img
        src={account.imageUrl}
        alt={account.name}
        title={title}
        className={cn(
          'size-[22px] rounded-full border-2 object-cover',
          '-ml-2 border-primary bg-primary-foreground first:ml-0',
        )}
      />
    );
  }
  return (
    <span
      title={title}
      className={cn(
        'grid size-[22px] place-items-center rounded-full border-2 text-[9px] font-extrabold',
        '-ml-2 border-primary first:ml-0',
      )}
      style={accountChipStyle(account)}
    >
      {account.initial}
    </span>
  );
}

/** Bank-brand colors are runtime DATA (Pluggy connector colors), not theme
 * tokens — the chip text just needs contrast against whatever arrives, so the
 * two text literals are deliberately theme-independent. Null color → neutral token. */
function accountChipStyle(account: AvailableToSpendAccount): {
  backgroundColor: string;
  color: string;
} {
  if (!account.color) {
    return { backgroundColor: 'var(--neutral-400)', color: 'var(--neutral-950)' };
  }
  const match = /^#([0-9a-f]{6})$/i.exec(account.color);
  if (!match) return { backgroundColor: account.color, color: '#FFFFFF' };
  const value = parseInt(match[1], 16);
  const yiq =
    (((value >> 16) & 255) * 299 + ((value >> 8) & 255) * 587 + (value & 255) * 114) / 1000;
  return { backgroundColor: account.color, color: yiq >= 150 ? '#1C1C1C' : '#FFFFFF' };
}

function BreakdownChip({
  label,
  amount,
  hiddenWhenZero,
}: {
  label: string;
  amount: string;
  hiddenWhenZero?: number;
}): ReactNode {
  if (hiddenWhenZero === 0) return null;
  return (
    <span
      className="rounded-lg bg-primary-foreground/15 px-2.5 py-1 text-primary-foreground/90"
    >
      {label} {amount}
    </span>
  );
}

function trackTimelineDayClick(): void {
  trackEvent('new_home.widget_action', { widget: 'disponivel', action: 'timeline_day' });
}

/** Cycle strip: one cell per day from the cycle's first day to the salary. Elapsed
 * days flag only overspending (muted otherwise); upcoming days carry the recurring
 * commitments + invoices; today wears the ring. A marker row floats above the strip
 * (prototype design) naming the cycle's important days: money in (salary/income) on
 * elapsed days, fatura / recurring bundles / expected salary ahead.
 * Desktop-only (sm+): the popover/keyboard controls don't work well on touch —
 * mobile keeps just the daily-goal footer until a touch-first design exists. */
function CommitmentsTimeline({ data }: { data: AvailableToSpend }): ReactNode {
  const windowEndLabel = formatDayMonth(data.windowEnd);
  const stripRef = useRef<HTMLDivElement>(null);

  // Roving keyboard navigation: Tab lands on today, ←/→ (and Home/End) walk the
  // interactive days, Enter/Space opens the focused day's details.
  const onStripKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const days = Array.from(
      stripRef.current?.querySelectorAll<HTMLButtonElement>('[data-timeline-day]') ?? [],
    );
    if (days.length === 0) return;
    const current = days.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home' ? 0
      : event.key === 'End' ? days.length - 1
      : event.key === 'ArrowLeft' ? (current <= 0 ? days.length - 1 : current - 1)
      : current >= days.length - 1 ? 0 : current + 1;
    days[next]?.focus();
    event.preventDefault();
  };

  return (
    <div className="relative mt-auto hidden pt-4 sm:block">
      <div className="mb-1.5 flex justify-between font-mono text-[10px] text-primary-foreground/75">
        <span>ciclo &amp; compromissos</span>
        {windowEndLabel ? (
          <span>{data.windowEndsOnSalary ? `salário ${windowEndLabel}` : `até ${windowEndLabel}`}</span>
        ) : null}
      </div>
      <div
        ref={stripRef}
        role="group"
        aria-label="Dias do ciclo — use as setas para navegar"
        onKeyDown={onStripKeyDown}
        className="flex gap-px"
      >
        {data.timeline.map((day) => (
          <TimelineDayColumn key={day.date} day={day} />
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[9px] text-primary-foreground/75">
        <span className="flex items-center gap-1">
          <span aria-hidden className="size-2 rounded-[2px] bg-primary-foreground/35" />
          no alvo
        </span>
        <span className="flex items-center gap-1">
          <span aria-hidden className="size-2 rounded-[2px] bg-error-400" />
          acima
        </span>
        <span className="flex items-center gap-1">
          <span aria-hidden className="size-2 rounded-[2px] bg-primary-foreground/60" />
          compromisso
        </span>
        {data.windowEndsOnSalary && (
          <span className="flex items-center gap-1">
            <span aria-hidden className="size-2 rounded-[2px] bg-primary-foreground" />
            salário
          </span>
        )}
        <span className="hidden sm:inline">toque num dia p/ ver detalhes</span>
      </div>
    </div>
  );
}

/** Cell fill per day. Elapsed days only FLAG the over-target ones (fixed bright
 * error-400 — the role tokens remap per theme and can melt into the dark card);
 * within-target days stay muted — "fine" is the default, not an achievement.
 * Future cells stay foreground-relative: commitments by presence, red once the
 * running balance stops covering them. */
function timelineCellClass(day: AvailableToSpendDay): string {
  if (day.kind !== 'future') {
    return day.status === 'over' ? 'bg-error-400' : 'bg-primary-foreground/35';
  }
  if (day.committed > 0) {
    return day.status === 'over' ? 'bg-error-400' : 'bg-primary-foreground/60';
  }
  return day.salary ? 'bg-primary-foreground' : 'bg-primary-foreground/15';
}

/**
 * One day column: marker slot on top, cell at the bottom. Hover shows the
 * dark-glass tooltip (the native `title` attribute was unreliable/slow);
 * clicking the cell OR its marker opens the day-details popover (money in/out,
 * commitments paid or due) — nothing navigates away anymore. Quiet future days
 * have no story yet, so they stay inert.
 */
function TimelineDayColumn({ day }: { day: AvailableToSpendDay }): ReactNode {
  const formatCurrency = useFormatCurrency();
  const [open, setOpen] = useState(false);
  const description = timelineDayDescription(day, formatCurrency);
  const markers = timelineDayMarkers(day);
  const interactive = isTimelineDayInteractive(day);

  const openDetails = (): void => {
    setOpen(true);
    trackTimelineDayClick();
  };

  const cellClass = cn(
    'h-[9px] w-full rounded-[2px]',
    timelineCellClass(day),
    day.kind === 'today' && 'ring-1 ring-primary-foreground',
    interactive && 'cursor-pointer transition-opacity hover:opacity-70',
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-end">
      <MarkerSlot markers={markers} onOpen={interactive ? openDetails : undefined} />
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipRoot delayDuration={150}>
          <TooltipTrigger asChild>
            {interactive ? (
              // The cell is the real PopoverTrigger so the panel anchors tight to
              // the day it describes. Roving tabindex: today is the strip's tab
              // stop; arrows move between days (handled by the strip container).
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-timeline-day
                  tabIndex={day.kind === 'today' ? 0 : -1}
                  aria-label={description}
                  onClick={trackTimelineDayClick}
                  className={cn(
                    cellClass,
                    'focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:outline-none',
                  )}
                />
              </PopoverTrigger>
            ) : (
              <span aria-label={description} className={cellClass} />
            )}
          </TooltipTrigger>
          <TooltipContent side="top">{description}</TooltipContent>
        </TooltipRoot>
        <PopoverContent
          side="top"
          sideOffset={6}
          collisionPadding={12}
          className="w-60 rounded-xl border-slate-700/60 bg-slate-900/95 p-3 text-slate-100 shadow-xl"
        >
          <DayDetails day={day} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** The contextual day panel: what entered and left on elapsed days, which
 * commitments were paid (past) or are due (future), and the salary note. */
function DayDetails({ day }: { day: AvailableToSpendDay }): ReactNode {
  const formatCurrency = useFormatCurrency();
  const dayLabel = formatDayMonth(day.date) ?? day.date;
  const elapsed = day.kind !== 'future';

  return (
    <div className="space-y-2.5">
      <p className="font-mono text-3xs font-semibold tracking-[0.12em] text-slate-400 uppercase">
        {dayLabel}
        {day.kind === 'today' ? ' · hoje' : ''}
      </p>

      {elapsed && (
        <div className="space-y-1 font-mono text-xs tabular-nums">
          <div className="flex items-center justify-between gap-4">
            <span className="text-slate-400">entrou</span>
            <span className="font-semibold text-success-300">
              {/* ?? 0 guards a stale cached payload from before the field existed. */}
              +{formatCurrency(day.entered ?? 0, { noCents: true })}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-slate-400">saiu</span>
            <span className="font-semibold text-error-300">
              −{formatCurrency(day.spent, { noCents: true })}
            </span>
          </div>
        </div>
      )}

      {day.labels.length > 0 && (
        <div>
          <p className="text-3xs font-medium text-slate-400 uppercase">
            {elapsed ? 'Pagos no dia' : 'A pagar'}
          </p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {day.labels.map((label) => (
              <li key={label} className="truncate">
                {label}
              </li>
            ))}
          </ul>
          {!elapsed && day.committed > 0 && (
            <p className="mt-1 font-mono text-xs font-semibold tabular-nums">
              {formatCurrency(day.committed, { noCents: true })}
            </p>
          )}
        </div>
      )}

      {day.salary && (
        <p className="text-xs font-medium text-info-300">
          {elapsed ? 'salário recebido' : 'salário esperado'}
        </p>
      )}

      {elapsed && (
        <Link
          href={`/transactions?period=custom&from=${day.date}&to=${day.date}`}
          className="inline-block text-2xs text-slate-300 underline underline-offset-2 hover:text-slate-100"
        >
          ver extrato
        </Link>
      )}
    </div>
  );
}

/** Marker chips use fixed bright steps (same rule as the heatmap): the role
 * tokens remap per theme and can melt into the dark-primary card. Green melts
 * into the emerald card and red over-alarms (user call), so money in is the
 * info blue (salary wears its category icon, "money") and anything due or
 * paid is the amber. */
const MARKER_ICON: Record<TimelineMarker['type'], { Icon: PhosphorIcon; className: string }> = {
  salario: { Icon: MoneyIcon, className: 'bg-info-300 text-neutral-950' },
  entrada: { Icon: CoinsIcon, className: 'bg-info-300 text-neutral-950' },
  despesa: { Icon: ArrowsClockwiseIcon, className: 'bg-warning-300 text-neutral-950' },
};

interface DayChip {
  key: string;
  Icon?: PhosphorIcon;
  label?: string;
  className: string;
}

/** A day's marker cluster, avatar-group style: one chip per category in the
 * fixed order, plus a neutral "+N" tail when the expense chip groups more
 * than one commitment. */
function markerChips(markers: TimelineMarker[]): DayChip[] {
  const chips: DayChip[] = [];
  for (const marker of markers) {
    const { Icon, className } = MARKER_ICON[marker.type];
    chips.push({
      key: marker.type,
      Icon: marker.type === 'despesa' && marker.isFatura ? CreditCardIcon : Icon,
      className,
    });
    if (marker.type === 'despesa' && marker.count > 1) {
      chips.push({
        key: 'more',
        label: `+${marker.count - 1}`,
        className: 'bg-primary-foreground/90 text-primary',
      });
    }
  }
  return chips;
}

/** Fixed-height slot above every cell so the strip stays aligned. The chips
 * overlap horizontally like an avatar group (earlier chips on top), with one
 * stem and one click target for the whole cluster. */
function MarkerSlot({
  markers,
  onOpen,
}: {
  markers: TimelineMarker[];
  onOpen?: () => void;
}): ReactNode {
  const chips = markerChips(markers);

  const cluster = chips.length > 0 && (
    <span className="flex flex-col items-center">
      <span className="flex items-center">
        {chips.map((chip, index) => (
          <span
            key={chip.key}
            data-testid={`timeline-marker-${chip.key}`}
            style={{ zIndex: chips.length - index }}
            className={cn(
              // The card-colored border makes the overlap read as a stack.
              'relative grid size-[18px] place-items-center rounded-full border-2 border-primary sm:size-6',
              index > 0 && '-ml-2 sm:-ml-2.5',
              chip.className,
            )}
          >
            {chip.Icon ? (
              <chip.Icon size={11} weight="fill" aria-hidden />
            ) : (
              <span className="font-mono text-[8px] font-bold sm:text-[9px]">{chip.label}</span>
            )}
          </span>
        ))}
      </span>
      <span aria-hidden className="h-[5px] w-px bg-primary-foreground/40" />
    </span>
  );

  return (
    <div className="relative mb-2 flex h-8 w-full items-end justify-center sm:h-9">
      {cluster && (
        <div className="z-10">
          {onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              aria-label="Ver detalhes do dia"
              className="cursor-pointer transition-opacity hover:opacity-80"
            >
              {cluster}
            </button>
          ) : (
            cluster
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Mobile-only footer — the dark hero trades the timeline for the daily goal.
 * Same min(pace, liquidity) meta as the Ritmo desktop footer (dailyMeta.ts), so
 * the number never swaps models between breakpoints. Alert tones use fixed
 * bright scale steps that read on the deep-emerald card (same rule as the
 * timeline heatmap) instead of the theme-remapped role tokens.
 */
function DailyGoalFooter({ data }: { data: AvailableToSpend }): ReactNode {
  const formatCurrency = useFormatCurrency();
  // SWR dedupes with the Ritmo widget's own fetch on the same page.
  const { data: pace } = useSpendingPace();
  const meta = selectDailyMeta(pace, data);
  if (!meta) return null;
  const parts = dailyMetaParts(meta, formatCurrency);

  return (
    <div className="relative mt-4 flex items-center gap-2.5 border-t border-primary-foreground/15 pt-3.5 sm:hidden">
      <span
        aria-hidden
        className={cn(
          'grid size-8 shrink-0 place-items-center rounded-full bg-primary-foreground/15',
          parts.tone === 'warning' && 'text-warning-300',
          parts.tone === 'danger' && 'text-error-300',
        )}
      >
        <TargetIcon size={15} weight="fill" />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="text-[11px] text-primary-foreground/70">{parts.title}</p>
        <p className="text-[13px] font-bold">
          {parts.lead}
          {parts.amount !== null && (
            <span
              className={cn(
                'font-mono tabular-nums',
                parts.tone === 'warning' && 'text-warning-300',
                parts.tone === 'danger' && 'text-error-300',
              )}
            >
              {parts.amount}
            </span>
          )}
          {parts.trail}
        </p>
      </div>
    </div>
  );
}
